import completeContactSchema from "@rentiohq/shared-frontend/dist/forms/addContact";
import {
  isPerformingSubmitActionSelector,
  performingSubmitActionErrorSelector,
  submitActionCreator,
} from "@rentiohq/shared-frontend/dist/forms/addContact/schema.addContact.actions";
import { EField as EContactField } from "@rentiohq/shared-frontend/dist/forms/addContact/schema.addContact.types";
import * as contactActions from "@rentiohq/shared-frontend/dist/redux/contact/contact.actions";
import * as contactSelectors from "@rentiohq/shared-frontend/dist/redux/contact/contact.selectors";
import { generateFormId } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import { IPartialRootState } from "@rentiohq/shared-frontend/dist/redux/types";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Loading,
  MultiStepForm,
  Modal,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import * as S from "./ConfirmContactDetailsModal.styled";

interface IProps {
  accountId: number;
  formData?: any;
  onClose: () => void;
  onSave: () => void;
}

export const ConfirmContactDetailsModal = (props: IProps) => {
  const dispatch = useDispatch();

  const { accountId, formData = {}, onClose, onSave } = props;
  const [formId] = React.useState(generateFormId());

  const contact = useSelector((state: IPartialRootState) =>
    contactSelectors.getContact(state, undefined, accountId),
  );

  const contactIsFetching = useSelector((state: IPartialRootState) =>
    contactSelectors.getContactIsFetching(state, undefined, accountId),
  );

  const contactFetchError = useSelector((state: IPartialRootState) =>
    contactSelectors.getContactFetchError(state, undefined, accountId),
  );

  React.useEffect(() => {
    dispatch(
      contactActions.getContactsByAccountIds.actions.start({
        accountIds: [accountId],
      }),
    );
  }, [accountId]);

  React.useEffect(() => {
    if (contactFetchError) {
      showAlert({
        type: "error",
        message: contactFetchError?.message,
      });
    }
  }, [contactFetchError]);

  const getFilePreviewLink = (file: File) => {
    if (file) {
      return URL.createObjectURL(file);
    }
  };
  const { payerIdentityDocuments } = formData;
  const idFront = payerIdentityDocuments[0]?.identityFrontDocument?.[0];
  const idBack = payerIdentityDocuments[0]?.identityBackDocument?.[0];

  if (!contact && contactIsFetching) {
    return <Loading />;
  }

  return (
    <Modal
      onClose={onClose}
      heading={getLocalizedText("confirm_contact_details.modal.heading", {
        role: getLocalizedText("role.tenant"),
      })}
      width="large"
    >
      <TextStyle size="large" variation="strong">
        {getLocalizedText("banqup.rent_deposit.payer_uploaded_ids.title")}
      </TextStyle>
      <S.ImagesContainer>
        {idFront && (
          <S.StyledImage src={getFilePreviewLink(idFront)} alt="id-front" />
        )}
        {idBack && (
          <S.StyledImage src={getFilePreviewLink(idBack)} alt="id-back" />
        )}
      </S.ImagesContainer>
      <MultiStepForm
        formId={`confirm-contact-details-${formId}`}
        schemas={completeContactSchema({
          contact,
          isCompany: !!contact?.isCompany,
          info: getLocalizedText("confirm_contact_details.modal.info"),
          fields: [
            EContactField.Firstname,
            EContactField.Lastname,
            EContactField.DateOfBirth,
          ],
        })}
        withAside={false}
        isPerformingSubmitActionSelector={isPerformingSubmitActionSelector(
          contact?.id,
        )}
        performingSubmitActionErrorSelector={performingSubmitActionErrorSelector(
          contact?.id,
        )}
        submitActionCreator={submitActionCreator(contact?.id)}
        onSuccess={() => {
          onSave();
          onClose();
        }}
        submitLabel={getLocalizedText("system.save")}
      />
    </Modal>
  );
};
